class CoveraxPageToc extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-page-toc-toggle]');
    this.labelEl = this.querySelector('[data-page-toc-label]');
    this.panel = this.querySelector('[data-page-toc-panel]');
    this.links = Array.from(this.querySelectorAll('[data-page-toc-link]'));
    this.defaultLabel = this.dataset.defaultLabel || this.labelEl?.textContent?.trim() || '';
    this.offset = Number(this.dataset.scrollOffset || 88);
    this.isOpen = false;
    this.isVisible = false;
    this.pastHero = false;
    this.footerBlocking = false;
    this.targets = [];
    this.scrollTicking = false;
    this.heroObserver = null;
    this.footerObserver = null;

    if (!this.toggle || !this.links.length) return;

    this.onToggle = this.handleToggle.bind(this);
    this.onDocPointer = this.handleDocPointer.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onLinkClick = this.handleLinkClick.bind(this);
    this.onScroll = this.handleScroll.bind(this);
    this.onResize = this.handleResize.bind(this);

    this.toggle.addEventListener('click', this.onToggle);
    document.addEventListener('pointerdown', this.onDocPointer);
    document.addEventListener('keydown', this.onKeydown);
    this.links.forEach((link) => link.addEventListener('click', this.onLinkClick));
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    this.collectTargets();
    this.setOpen(false);
    this.setupHeroReveal();
    this.setupFooterHide();
    this.updateActiveFromScroll();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggle);
    document.removeEventListener('pointerdown', this.onDocPointer);
    document.removeEventListener('keydown', this.onKeydown);
    this.links.forEach((link) => link.removeEventListener('click', this.onLinkClick));
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.heroObserver?.disconnect();
    this.footerObserver?.disconnect();
  }

  findHero() {
    const selector = this.dataset.heroSelector || '';
    if (selector) {
      return document.querySelector(selector);
    }

    return (
      document.querySelector('.cx-acc-hero') ||
      document.querySelector('.coverax-home-hero') ||
      document.querySelector('[data-page-toc-hero]') ||
      document.querySelector('#MainContent .shopify-section')
    );
  }

  findFooter() {
    return (
      document.querySelector('.coverax-footer .footer__bottom') ||
      document.querySelector('.coverax-footer') ||
      document.querySelector('footer')
    );
  }

  setupHeroReveal() {
    this.hero = this.findHero();

    if (!this.hero) {
      this.pastHero = true;
      this.syncVisibility();
      return;
    }

    if ('IntersectionObserver' in window) {
      this.heroObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          // Einblenden, sobald der Hero den unteren Sichtbereich verlassen hat
          this.pastHero = !entry.isIntersecting;
          this.syncVisibility();
        },
        {
          root: null,
          threshold: 0,
          rootMargin: '0px 0px -42% 0px',
        }
      );
      this.heroObserver.observe(this.hero);
      this.updateVisibilityFromHero();
      return;
    }

    this.updateVisibilityFromHero();
  }

  setupFooterHide() {
    this.footer = this.findFooter();
    if (!this.footer) {
      this.footerBlocking = false;
      this.syncVisibility();
      return;
    }

    if ('IntersectionObserver' in window) {
      // Früher ausblenden: Pill-Höhe + Abstand, bevor Footer-Bottom überdeckt wird
      this.footerObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          this.footerBlocking = entry.isIntersecting;
          this.syncVisibility();
        },
        {
          root: null,
          threshold: 0,
          rootMargin: '0px 0px 96px 0px',
        }
      );
      this.footerObserver.observe(this.footer);
      this.updateVisibilityFromFooter();
      return;
    }

    this.updateVisibilityFromFooter();
  }

  updateVisibilityFromHero() {
    if (!this.hero) {
      this.pastHero = true;
      this.syncVisibility();
      return;
    }

    const heroBottom = this.hero.getBoundingClientRect().bottom;
    this.pastHero = heroBottom < window.innerHeight * 0.58;
    this.syncVisibility();
  }

  updateVisibilityFromFooter() {
    if (!this.footer) {
      this.footerBlocking = false;
      this.syncVisibility();
      return;
    }

    const rect = this.footer.getBoundingClientRect();
    // Gleiche Vorlauf-Logik wie rootMargin 96px
    this.footerBlocking = rect.top < window.innerHeight + 96 && rect.bottom > 0;
    this.syncVisibility();
  }

  syncVisibility() {
    this.setVisible(this.pastHero && !this.footerBlocking);
  }

  collectTargets() {
    this.targets = this.links
      .map((link) => {
        const href = link.getAttribute('href') || '';
        if (!href.startsWith('#') || href === '#') return null;
        const id = decodeURIComponent(href.slice(1));
        const el = document.getElementById(id);
        if (!el) return null;
        return { link, el, label: link.textContent.trim() };
      })
      .filter(Boolean);
  }

  handleToggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isVisible) return;
    this.setOpen(!this.isOpen);
  }

  handleDocPointer(event) {
    if (!this.isOpen) return;
    if (this.contains(event.target)) return;
    this.setOpen(false);
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.setOpen(false);
      this.toggle?.focus();
    }
  }

  handleLinkClick(event) {
    const link = event.currentTarget;
    const href = link.getAttribute('href') || '';

    if (!href.startsWith('#') || href === '#') {
      this.setOpen(false);
      return;
    }

    const id = decodeURIComponent(href.slice(1));
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    this.setOpen(false);
    this.setActive(link, link.textContent.trim());

    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - this.offset);
    window.scrollTo({ top, behavior: 'smooth' });

    if (history.replaceState) {
      history.replaceState(null, '', `#${id}`);
    }
  }

  handleScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    window.requestAnimationFrame(() => {
      if (!this.heroObserver) {
        this.updateVisibilityFromHero();
      }
      if (!this.footerObserver) {
        this.updateVisibilityFromFooter();
      }
      this.updateActiveFromScroll();
      this.scrollTicking = false;
    });
  }

  handleResize() {
    this.collectTargets();
    this.updateVisibilityFromHero();
    this.updateVisibilityFromFooter();
  }

  updateActiveFromScroll() {
    if (!this.targets.length) {
      this.setActive(null, this.defaultLabel);
      return;
    }

    const marker = this.offset + 12;
    let current = null;

    for (const item of this.targets) {
      const top = item.el.getBoundingClientRect().top;
      if (top <= marker) {
        current = item;
      }
    }

    if (current) {
      this.setActive(current.link, current.label);
    } else {
      this.setActive(null, this.defaultLabel);
    }
  }

  setActive(activeLink, label) {
    this.links.forEach((link) => {
      link.classList.toggle('is-active', link === activeLink);
    });

    if (this.labelEl && label) {
      this.labelEl.textContent = label;
    }
  }

  setVisible(visible) {
    if (this.isVisible === visible) return;

    this.isVisible = visible;
    this.classList.toggle('is-visible', visible);
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');

    if (!visible && this.isOpen) {
      this.setOpen(false);
    }
  }

  setOpen(open) {
    this.isOpen = open;
    this.classList.toggle('is-open', open);
    this.toggle?.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (this.panel) {
      this.panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) {
        this.panel.removeAttribute('inert');
      } else {
        this.panel.setAttribute('inert', '');
      }
    }
  }
}

if (!customElements.get('coverax-page-toc')) {
  customElements.define('coverax-page-toc', CoveraxPageToc);
}
